import type { IOrderExperiment } from '~/modules/order-analytic';
import type { RendererProps } from '../type';

import { Box, Button, Icon, InlineStack, Popover, Scrollable, Text } from '@shopify/polaris';
import { GTextTruncated, ModalType, useGModalContext } from '~/presentation/ui';
import { CaretDownIcon, ChartLineIcon } from '@shopify/polaris-icons';
import { CampaignsCellVersion } from './CampaignsCellVersion';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { cls } from '~/utils';

export const CampaignsCell = ({
  value,
  campaignsCellProps,
}: Pick<RendererProps<IOrderExperiment[]>, 'value' | 'campaignsCellProps'>) => {
  const { orderId = '', isHideCampaignContent } = campaignsCellProps ?? {};

  const { t } = useTranslation();

  const { onOpenModal } = useGModalContext<ModalType.ORDER_JOURNEY>();

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const campaignLeadingToOrder = value.at(0);
  const isShowPopupCampaigns = value.length > 1 && !isHideCampaignContent;

  const handleViewOrderJourney = () => {
    onOpenModal(ModalType.ORDER_JOURNEY, { id: orderId, orderId });
  };

  if (!campaignLeadingToOrder) return null;

  return (
    <Popover
      active={active}
      activator={
        <div
          className={cls(
            'GReportTable__CellContent--clickable flex items-center w-full h-full px-2',
            {
              'cursor-pointer': isShowPopupCampaigns,
              'hover:!bg-[#f7f7f7]': !isShowPopupCampaigns,
            },
          )}
          onClick={isShowPopupCampaigns ? toggleActive : undefined}
        >
          <CampaignsCellVersion version={campaignLeadingToOrder.version} />

          {!isHideCampaignContent && (
            <>
              <Box minWidth="0" paddingInlineStart={campaignLeadingToOrder.version ? '200' : '0'}>
                <GTextTruncated
                  as="span"
                  variant="bodyMd"
                  tone="subdued"
                  useWrapper
                  tooltip={{ hoverDelay: 300 }}
                >
                  {campaignLeadingToOrder.campaign_group_name ||
                    campaignLeadingToOrder.campaign_name}
                </GTextTruncated>
              </Box>

              {isShowPopupCampaigns && (
                <span className="opacity-0 group-hover/GReportTable__Row:opacity-100">
                  <Icon source={CaretDownIcon} />
                </span>
              )}
            </>
          )}
        </div>
      }
      onClose={toggleActive}
    >
      <Box padding="0" width="340px" overflowX="hidden">
        <Box padding="300">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {t('Experiments visited before order')}
          </Text>
        </Box>

        <Box borderBlockEndWidth="025" borderBlockStartWidth="025" borderColor="border-secondary">
          <Scrollable vertical scrollbarWidth="thin" horizontal={false} className={'max-h-[216px]'}>
            <Box paddingBlock="200">
              {value.map((campaign, index) => {
                return (
                  <Box key={index} paddingInline="300" paddingBlock="200">
                    <Box paddingBlock="050">
                      <InlineStack gap="200" wrap={false}>
                        <CampaignsCellVersion version={campaign.version} />

                        <Box minWidth="0">
                          <GTextTruncated as="span" variant="bodyMd" tone="subdued" useWrapper>
                            {campaign.campaign_group_name || campaign.campaign_name}
                          </GTextTruncated>
                        </Box>
                      </InlineStack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Scrollable>
        </Box>

        <Box padding="300">
          <Button icon={ChartLineIcon} fullWidth onClick={handleViewOrderJourney}>
            {t('View order journey')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
